import { CommonReducerState } from '@/features/line-items/line-item-form/common/form-reducer';
import { UTCDate } from '@date-fns/utc';
import { isSameDay } from 'date-fns';
import { addDays } from 'shared/src/date-string-utils/date-utils';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';

export function syncLifetimeBlock<T extends CommonReducerState>(
  state: T,
  budget: number | undefined
): T {
  const { dates, pacing_type, pacing_blocks } = state.formData;
  if (pacing_type !== 'lifetime') return state;
  if (budget == undefined) return { ...state, monthly_rollover: true };
  return {
    ...state,
    formData: {
      ...state.formData,
      pacing_blocks: [{ dates, budget, origin: pacing_blocks[0]?.origin ?? 'pending' }],
      monthly_rollover: true
    }
  };
}

export function addPacingBlock<T extends CommonReducerState>(state: T): T {
  const { dates, pacing_blocks } = state.formData;

  if (pacing_blocks.length === 0)
    return {
      ...state,
      formData: {
        ...state.formData,
        pacing_blocks: [{ dates, budget: 0, origin: 'pending' }]
      }
    };

  const last = pacing_blocks[pacing_blocks.length - 1]!;
  const lastTo = new UTCDate(last.dates.to);
  const to = new UTCDate(dates.to);

  return {
    ...state,
    formData: {
      ...state.formData,
      pacing_blocks: [
        ...pacing_blocks,
        {
          dates: {
            from: isSameDay(lastTo, to) ? dates.to : addDays(last.dates.to, 1),
            to: dates.to
          },
          budget: 0,
          origin: 'pending'
        }
      ]
    }
  };
}

export function removePacingBlock<T extends CommonReducerState>(state: T, index: number): T {
  return {
    ...state,
    formData: {
      ...state.formData,
      pacing_blocks: state.formData.pacing_blocks.filter((_b, idx) => idx !== index)
    }
  };
}

export function clearPacingBlocks<T extends CommonReducerState>(state: T): T {
  return {
    ...state,
    formData: {
      ...state.formData,
      pacing_blocks: []
    }
  };
}

export function updatePacingBlock<T extends CommonReducerState>(
  state: T,
  index: number,
  patch: Partial<PacingBlockV2>
): T {
  const { pacing_blocks } = state.formData;
  const existing = pacing_blocks[index];
  if (!existing) return state;

  const blocks = pacing_blocks.slice();
  blocks[index] = { ...existing, ...patch };
  return {
    ...state,
    formData: {
      ...state.formData,
      pacing_blocks: blocks
    }
  };
}
